// @flow
import { FormBuilderContext } from 'pages/Contexts/FormBuilderContext'
import * as React from 'react'
import Ui from 'ui'
import Space from 'ui/atoms/Space'

type Props = {
  question: Object,
  questionType: string,
  questionSubtype: string,
}

const FORMAT_OPTIONS = [
  {
    label: 'Side by Side',
    value: 'horizontal_list',
  },
  {
    label: 'One column',
    value: 'one_column',
  },
  {
    label: 'Two columns',
    value: 'two_columns',
  },
  {
    label: 'Three columns',
    value: 'three_columns',
  },
  {
    label: 'Four columns',
    value: 'four_columns',
  },
]

function BuilderSideMenuEditQuestionSubtypeForChoice({
  question,
  questionType,
  questionSubtype,
}: Props): React.Node {
  const { selectedItem, setQuestion } = React.useContext(FormBuilderContext)

  const [hasFormat, setHasFormat] = React.useState(
    !['dropdown', 'dropdown_multiple'].includes(questionSubtype)
  )
  React.useEffect(() => {
    setHasFormat(!['dropdown', 'dropdown_multiple'].includes(questionSubtype))
  }, [questionSubtype])

  const getFormatValue = React.useCallback(() => {
    if (questionSubtype === 'horizontal_list') return 'horizontal_list'
    if (questionSubtype === 'vertical_list') {
      if (question?.num_columns === 1) {
        return 'one_column'
      }
      if (question?.num_columns === 2) {
        return 'two_columns'
      }
      if (question?.num_columns === 3) {
        return 'three_columns'
      }
      if (question?.num_columns >= 4) {
        return 'four_columns'
      }
    }
  }, [question, questionSubtype])
  const [formatValue, setFormatValue] = React.useState(getFormatValue)
  React.useEffect(() => {
    setFormatValue(getFormatValue)
  }, [getFormatValue])

  const onSetRadio = React.useCallback(() => {
    setQuestion(selectedItem.id, {
      ...question,
      default_value: null,
      question_type: {
        ...question.question_type,
        question_type: 'radio',
        question_subtype: !['dropdown', 'dropdown_multiple'].includes(
          questionSubtype
        )
          ? questionSubtype
          : 'vertical_list',
      },
    })
  }, [question, questionSubtype, selectedItem, setQuestion])

  const onSetCheckbox = React.useCallback(() => {
    setQuestion(selectedItem.id, {
      ...question,
      default_value: null,
      question_type: {
        ...question.question_type,
        question_type: 'checkbox',
        question_subtype: !['dropdown', 'dropdown_multiple'].includes(
          questionSubtype
        )
          ? questionSubtype
          : 'vertical_list',
      },
    })
  }, [question, questionSubtype, selectedItem, setQuestion])

  const onSetDropdown = React.useCallback(() => {
    setQuestion(selectedItem.id, {
      ...question,
      default_value: null,
      question_type: {
        ...question.question_type,
        question_type: 'radio',
        question_subtype: 'dropdown',
      },
    })
  }, [question, selectedItem, setQuestion])

  const onSetDropdownMultiple = React.useCallback(() => {
    setQuestion(selectedItem.id, {
      ...question,
      question_type: {
        ...question.question_type,
        question_type: 'radio',
        question_subtype: 'dropdown_multiple',
      },
    })
  }, [question, selectedItem, setQuestion])

  const onChangeFormat = React.useCallback(
    (formatType) => {
      switch (formatType) {
        case 'horizontal_list':
          return setQuestion(selectedItem.id, {
            ...question,
            question_type: {
              ...question.question_type,
              question_subtype: 'horizontal_list',
            },
          })
        case 'one_column':
          return setQuestion(selectedItem.id, {
            ...question,
            num_columns: 1,
            question_type: {
              ...question.question_type,
              question_subtype: 'vertical_list',
            },
          })
        case 'two_columns':
          return setQuestion(selectedItem.id, {
            ...question,
            num_columns: 2,
            question_type: {
              ...question.question_type,
              question_subtype: 'vertical_list',
            },
          })
        case 'three_columns':
          return setQuestion(selectedItem.id, {
            ...question,
            num_columns: 3,
            question_type: {
              ...question.question_type,
              question_subtype: 'vertical_list',
            },
          })
        case 'four_columns':
          return setQuestion(selectedItem.id, {
            ...question,
            num_columns: 4,
            question_type: {
              ...question.question_type,
              question_subtype: 'vertical_list',
            },
          })
        default:
          break
      }
    },
    [question, selectedItem, setQuestion]
  )

  return (
    <Space>
      <div>
        <Ui.QuestionTitle>Type</Ui.QuestionTitle>
        <Ui.Radio
          checked={
            questionType === 'radio' &&
            !['dropdown', 'dropdown_multiple'].includes(questionSubtype)
          }
          onChange={onSetRadio}
        >
          Single Choice
        </Ui.Radio>
        <Ui.Radio
          checked={questionType === 'checkbox'}
          onChange={onSetCheckbox}
        >
          Multiple Choice
        </Ui.Radio>
        <Ui.Radio
          checked={questionType === 'radio' && questionSubtype === 'dropdown'}
          onChange={onSetDropdown}
        >
          Dropdown
        </Ui.Radio>
        <Ui.Radio
          checked={
            questionType === 'radio' && questionSubtype === 'dropdown_multiple'
          }
          onChange={onSetDropdownMultiple}
        >
          Dropdown Multiple
        </Ui.Radio>
      </div>
      {hasFormat && (
        <Ui.Dropdown
          label="Layout"
          onChange={onChangeFormat}
          options={FORMAT_OPTIONS}
          placeholder="Select a Layout"
          value={formatValue}
        />
      )}
    </Space>
  )
}

export default BuilderSideMenuEditQuestionSubtypeForChoice
